<template>
  <div class="status-page">

    <header class="who">
      <div class="avatar">
        <img v-if="photo" :src="photo" :alt="name">
        <span v-else class="initials">{{ initials }}</span>
      </div>
      <div>
        <h1 class="name">{{ name }}</h1>
        <p class="email">{{ email }}</p>
      </div>
    </header>

    <div class="form">
      <div class="col">
        <h2 class="label">Change Status</h2>

        <div class="select-wrap">
          <select v-model="chosen" class="input">
            <option value="">Pick a status</option>
            <option v-for="s in types" :key="s.value" :value="s.value">
              {{ s.icon }}  {{ s.label }}
            </option>
          </select>
        </div>
      </div>

      <div class="col">
        <h2 class="label">Date</h2>

        <div class="dates">
          <input v-model="from" type="date" class="input" aria-label="From">
          <span class="to">to</span>
          <input v-model="to" type="date" class="input" aria-label="To">
        </div>

        <!-- A single day is the same date twice, which is not obvious, so the
             common case gets its own control rather than making people work it
             out. -->
        <button class="link" @click="singleDay">Just one day</button>
      </div>

      <!-- Only for a status that asks for them.
           A day worked from home produces no badge events, so the log has no
           hours for it. These are the only way to have any: Vero cannot observe
           a home office, and assuming a standard day would put a number on the
           screen that looks measured and is not.
           Which statuses ask is set per status under Admin > Statuses, rather
           than wired to one code that a rename would quietly break. -->
      <div v-if="asksForHours" class="col">
        <h2 class="label">Working hours</h2>

        <div class="dates">
          <!-- A dropdown rather than a time input.
               
               <input type="time"> renders in the BROWSER's locale, so an
               English machine shows "04:00 PM" however the rest of Vero is
               written, and there is no way to override it. A select shows what
               it is given.
               
               Quarter hours because nobody records starting at 08:07, and a
               shorter list is quicker to pick from than a free-text field is
               to type into. -->
          <select v-model="startTime" class="input" aria-label="From time">
            <option value="">Not set</option>
            <option v-for="t in TIMES" :key="t" :value="t">{{ t }}</option>
          </select>

          <span class="to">to</span>

          <select v-model="endTime" class="input" aria-label="To time">
            <option value="">Not set</option>
            <option v-for="t in TIMES" :key="t" :value="t">{{ t }}</option>
          </select>
        </div>

        <p class="hint">
          Record when you start and add the end time when you finish. The end
          can be left blank for now.
        </p>
      </div>
    </div>

    <div class="actions">
      <p v-if="formError" class="msg err">{{ formError }}</p>
      <p v-else-if="saved" class="msg ok">Saved</p>
      <span class="spacer"></span>
      <button class="save" :disabled="!canSave || saving" @click="save">
        {{ saving ? 'Saving…' : 'Save' }}
      </button>
    </div>

    <!-- What is already set. Without this there is no way to see or undo an
         entry, and a wrong date range would be permanent. -->
    <section class="existing">
      <h2 class="section-title">Upcoming</h2>

      <p v-if="loading" class="msg">Reading your statuses</p>
      <p v-else-if="loadError" class="msg err">{{ loadError }}</p>
      <p v-else-if="mine.length === 0" class="msg">Nothing set</p>

      <ul v-else class="entries">
        <li v-for="entry in mine" :key="entry.id" class="entry">
          <span class="entry-icon">{{ iconFor(entry.status) }}</span>
          <span class="entry-label">{{ labelFor(entry.status) }}</span>
          <span class="entry-dates">{{ formatRange(entry) }}</span>

          <!-- Closing an open day.
               A start time with no end is the normal state during the day: you
               know when you began and not yet when you will stop. This is where
               it gets finished, rather than asking for a guess in the morning. -->
          <span v-if="isOpen(entry)" class="entry-close">
            <span class="entry-from">from {{ entry.start_time }}</span>
            <select
              class="entry-time"
              :value="endDraft[entry.id] ?? ''"
              aria-label="End time"
              @change="endDraft[entry.id] = $event.target.value"
            >
              <option value="">Finish at…</option>
              <option v-for="t in TIMES" :key="t" :value="t">{{ t }}</option>
            </select>

            <!-- A button rather than a link.
                 It sat next to Remove looking like secondary text, and the Save
                 button above belongs to the form for a NEW status, so there was
                 nothing here that looked like the way to finish the day. -->
            <button
              class="finish"
              :disabled="!endDraft[entry.id] || closing === entry.id"
              @click="close(entry)"
            >{{ closing === entry.id ? 'Saving…' : 'Finish' }}</button>
          </span>

          <span v-else-if="entry.start_time" class="entry-hours">
            {{ entry.start_time }}&ndash;{{ entry.end_time }}
          </span>

          <button class="link danger" @click="remove(entry)">Remove</button>
        </li>
      </ul>
    </section>

  </div>
</template>


<script setup>
import { ref, computed, onMounted } from 'vue'
import { authState } from '@/auth/authService'
import {
  getStatusTypes, statusTypes, statusMeta,
  getMyStatuses, setMyStatus, deleteMyStatus, setMyStatusEndTime
} from '@/services/StatusService'


const name = computed(() => authState.account?.name ?? 'Your status')
const email = computed(() => authState.account?.username ?? '')
const photo = ref(null)

const initials = computed(() => {
  const parts = (name.value || '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
})

function isoDate(d) { return d.toISOString().slice(0, 10) }

const chosen = ref('')
// Every quarter hour of the day, 24-hour. Built once rather than written out:
// 96 literals would be a place for a typo to hide.
const TIMES = (() => {
  const out = []
  for (let minutes = 0; minutes < 24 * 60; minutes += 15) {
    const h = String(Math.floor(minutes / 60)).padStart(2, '0')
    const m = String(minutes % 60).padStart(2, '0')
    out.push(`${h}:${m}`)
  }
  return out
})()

const endDraft = ref({})
const closing = ref(null)

const startTime = ref('')
const endTime = ref('')

const from = ref(isoDate(new Date()))
const to = ref(isoDate(new Date()))

const mine = ref([])
const loading = ref(true)
const loadError = ref(null)
const saving = ref(false)
const formError = ref(null)
const saved = ref(false)

const canSave = computed(() =>
  chosen.value !== '' && from.value !== '' && to.value !== '' && to.value >= from.value
)

function singleDay() {
  to.value = from.value
}

// The choices come from the table now, not a constant compiled into the app.
// Seeded with whatever the service already has cached so the select is never
// briefly empty, then replaced when the fetch returns.
const types = ref(statusTypes())

// Whether the chosen status wants working hours. Read from the status itself,
// so adding a second remote-working status needs no code change.
const asksForHours = computed(() =>
  types.value.find((t) => t.value === chosen.value)?.asksForHours === true
)

// Started but not finished. The end time is the only thing missing, and it is
// the one thing that could not be known when the entry was made.
function isOpen(entry) {
  return Boolean(entry.start_time) && !entry.end_time
}

async function close(entry) {
  closing.value = entry.id
  formError.value = null

  try {
    await setMyStatusEndTime(entry.id, endDraft.value[entry.id])
    await load()
  } catch (err) {
    console.error(err)
    formError.value = err.message ?? String(err)
  } finally {
    closing.value = null
  }
}

function iconFor(value) { return statusMeta(value)?.icon ?? '' }
function labelFor(value) { return statusMeta(value)?.label ?? value }

function formatRange(entry) {
  const opts = { day: 'numeric', month: 'short' }
  const start = new Date(entry.start_date + 'T00:00:00')
  const end = new Date(entry.end_date + 'T00:00:00')

  if (entry.start_date === entry.end_date) {
    return start.toLocaleDateString(undefined, opts)
  }
  return `${start.toLocaleDateString(undefined, opts)} \u2013 ${end.toLocaleDateString(undefined, opts)}`
}

async function load() {
  loading.value = true
  loadError.value = null

  try {
    mine.value = await getMyStatuses()
  } catch (err) {
    console.error(err)
    loadError.value = err.message ?? String(err)
  } finally {
    loading.value = false
  }
}

async function save() {
  saving.value = true
  formError.value = null
  saved.value = false

  try {
    await setMyStatus(chosen.value, from.value, to.value, startTime.value, endTime.value)
    chosen.value = ''
    startTime.value = ''
    endTime.value = ''
    saved.value = true
    await load()
  } catch (err) {
    console.error(err)
    formError.value = err.message ?? String(err)
  } finally {
    saving.value = false
  }
}

async function remove(entry) {
  formError.value = null

  try {
    await deleteMyStatus(entry.id)
    await load()
  } catch (err) {
    console.error(err)
    formError.value = err.message ?? String(err)
  }
}


onMounted(async () => {
  // Not awaited together with load(): the statuses are cached after the first
  // call and a failure here should not stop somebody seeing the entries they
  // already have.
  getStatusTypes()
    .then((t) => { types.value = t })
    .catch((err) => console.warn('could not load status types', err))

  await load()
})
</script>


<style scoped>
.status-page {
  --ink: #003c6c;
  --ink-soft: #64748b;
  --accent: #ed6e01;
  --line: #cfe0ea;
  --wash: #f7f9fb;
  --paper: #ffffff;

  /* Left aligned, not centred. Centring made the content drift right of the
     menu and the logo, so nothing lined up with anything else. The max-width
     stays: it stops a line of text running the width of a wide monitor. */
  max-width: 1000px;
  margin: 0;
  padding: 24px 24px 80px;
  text-align: left;
  color: #191c1e;
}


/* ---------- Who ---------- */

.who {
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 28px 0 44px;
}

.avatar {
  flex: none;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: 2px solid var(--ink);
  display: grid;
  place-items: center;
  overflow: hidden;
}

.avatar img { width: 100%; height: 100%; object-fit: cover; }

.initials {
  font-size: 18px;
  font-weight: 700;
  color: var(--ink);
}

.name {
  font-size: 28px;
  font-weight: 600;
  margin: 0;
  color: #191c1e;
}

.email {
  font-size: 16px;
  color: var(--ink);
  text-decoration: underline;
  margin: 2px 0 0;
}

/* ---------- Form ---------- */

.form {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 46px;
}

.label {
  font-size: 19px;
  font-weight: 400;
  color: var(--ink);
  margin: 0 0 12px;
}

.input {
  font-family: inherit;
  font-size: 15px;
  height: 46px;
  width: 100%;
  padding: 0 14px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--paper);
  color: #191c1e;
  outline: none;
  box-sizing: border-box;
}

.input:focus { border-color: var(--ink); }

.dates {
  display: flex;
  align-items: center;
  gap: 10px;
}

.entry-close,
.entry-hours {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.entry-from,
.entry-hours {
  font-size: 13.5px;
  color: #64748b;
  font-variant-numeric: tabular-nums;
}

.entry-time {
  font-family: inherit;
  font-variant-numeric: tabular-nums;
  font-size: 14px;
  height: 34px;
  padding: 0 8px;
  border: 1px solid #dfe4e9;
  border-radius: 8px;
  background: #fff;
  outline: none;
}

.entry-time:focus { border-color: #003c6c; }

.finish {
  font-family: inherit;
  font-size: 13.5px;
  font-weight: 600;
  height: 34px;
  padding: 0 16px;
  border: 1px solid #003c6c;
  border-radius: 8px;
  background: #003c6c;
  color: #fff;
  cursor: pointer;
}

.finish:hover:not(:disabled) { background: #00548f; }

/* Greyed until the time field holds a complete value. A time input reports an
   empty string while it is half-typed, so the button can look wrong when the
   field looks filled; disabled is the honest state. */
.finish:disabled {
  opacity: 0.35;
  cursor: default;
}

.hint {
  font-size: 13px;
  line-height: 1.5;
  color: #64748b;
  margin: 10px 0 0;
  max-width: 46ch;
}

.to {
  font-size: 14px;
  color: var(--ink-soft);
  flex: none;
}

/* ---------- Actions ---------- */

.actions {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 40px;
}

.spacer { flex: 1; }

.save {
  font-family: inherit;
  font-size: 15px;
  font-weight: 500;
  color: #ffffff;
  background: var(--accent);
  border: none;
  border-radius: 4px;
  padding: 11px 40px;
  cursor: pointer;
}

.save:hover:not(:disabled) { background: #cf6101; }
.save:disabled { opacity: 0.45; cursor: default; }

/* ---------- Existing ---------- */

.existing {
  margin-top: 54px;
  padding-top: 26px;
  border-top: 1px solid #e6ebef;
}

.section-title {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-soft);
  margin: 0 0 14px;
}

.entries {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.entry {
  display: flex;
  align-items: center;
  gap: 14px;
  border: 1px solid #e6ebef;
  border-radius: 8px;
  padding: 12px 16px;
}

.entry-icon { font-size: 19px; }

.entry-label {
  font-size: 15px;
  font-weight: 600;
  color: var(--ink);
  min-width: 170px;
}

.entry-dates {
  font-size: 15px;
  color: var(--ink-soft);
  flex: 1;
}

/* ---------- Messages ---------- */

.msg {
  font-size: 15px;
  color: var(--ink-soft);
  margin: 0;
}

.msg.err { color: #b3261e; }
.msg.ok { color: #0f9d58; }

.link {
  font-family: inherit;
  font-size: 14px;
  color: var(--ink);
  background: none;
  border: none;
  padding: 0;
  margin-top: 10px;
  text-decoration: underline;
  cursor: pointer;
}

.link.danger { color: #b3261e; margin-top: 0; }

@media (max-width: 760px) {
  .status-page { padding: 20px 20px 60px; }
  .form { grid-template-columns: minmax(0, 1fr); gap: 30px; }
  .entry { flex-wrap: wrap; }
  .entry-label { min-width: 0; }
}
</style>
